import { type AiAgentId, type ChatMessage } from '@fruitful/llm-service-shared';
import { WidgetType } from '@fruitful/types';
import { BodySmallRegular, ScrollView, YStack } from 'smoothie';

import { useGetMastraThreadTranscript } from '../hooks/useGetMastraThreadTranscript';
import { useLiveThreadRefetchInterval } from '../hooks/useLiveThreadRefetchInterval';
import { type TranscriptWidgetSources } from '../utils/transcriptWidgetSources';
import { TranscriptWidget } from './widgets/TranscriptWidget';

type EntrySource = 'agent' | 'participant' | 'widget';

const SOURCE_COLORS: Record<EntrySource, string> = {
    agent: '$green900',
    participant: '$blue900',
    widget: '$orange900',
};

interface RenderedEntry {
    source: EntrySource;
    label: string;
    content: string;
    widget?: WidgetType;
    widgetProps?: Record<string, unknown> | null;
    widgetSources?: TranscriptWidgetSources;
}

/**
 * Override display labels for widget types in the transcript. When a widget's
 * identity depends on its props (e.g. multi-select with a savings responseKey),
 * the resolver inspects props to return a more specific label.
 */
const resolveWidgetLabel = (
    widget: WidgetType,
    props: Record<string, unknown> | null | undefined,
): string => {
    if (
        widget === WidgetType.MultiSelectInput &&
        props?.responseKey === 'goalsSavings.goalType'
    ) {
        return 'Savings Goals';
    }
    return widget;
};

/** Flatten one Mastra chat message into its rendered transcript entries. */
export const chatMessageToRendered = (
    message: ChatMessage,
    widgetSources?: TranscriptWidgetSources,
): RenderedEntry[] => {
    const entries: RenderedEntry[] = [];

    for (const part of message.content) {
        if (part.type === 'ui') {
            entries.push({
                source: 'widget',
                label: resolveWidgetLabel(part.widget, part.props),
                content: '',
                widget: part.widget,
                widgetProps: part.props,
                widgetSources,
            });
            continue;
        }

        const text = part.text.trim();

        entries.push({
            source: message.role === 'assistant' ? 'agent' : 'participant',
            label: message.role === 'assistant' ? 'Agent' : 'Member',
            content: text === '' ? '[empty message]' : text,
        });
    }

    return entries;
};

export const chatMessagesToEntries = (
    messages: ChatMessage[],
    widgetSources?: Map<string, TranscriptWidgetSources>,
): RenderedEntry[] =>
    [...messages]
        .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
        .flatMap((message) =>
            chatMessageToRendered(message, widgetSources?.get(message.id)),
        );

/** Render the actual widget for a transcript entry, when supported. */
const RenderedWidget = ({
    entry,
    memberUserId,
}: {
    entry: RenderedEntry;
    memberUserId?: string | null;
}) => {
    if (entry.widget == null) {
        return null;
    }

    return (
        <YStack paddingLeft="$2" paddingTop="$1">
            <TranscriptWidget
                widget={entry.widget}
                props={entry.widgetProps ?? null}
                sources={entry.widgetSources}
                memberUserId={memberUserId}
            />
        </YStack>
    );
};

const TranscriptEntry = ({
    entry,
    memberUserId,
}: {
    entry: RenderedEntry;
    memberUserId?: string | null;
}) => (
    <YStack gap="$1">
        <BodySmallRegular color={SOURCE_COLORS[entry.source]} fontWeight="700">
            {entry.label}
        </BodySmallRegular>
        {entry.content ? (
            <BodySmallRegular color="$gray900" paddingLeft="$2">
                {entry.content}
            </BodySmallRegular>
        ) : null}
        <RenderedWidget entry={entry} memberUserId={memberUserId} />
    </YStack>
);

export const AiThreadTranscriptPanel = ({
    threadId,
    agentId,
    memberUserId,
}: {
    threadId: string | undefined;
    agentId: AiAgentId;
    /** Picks the member's own card when a widget renders a joint account. */
    memberUserId?: string | null;
}) => {
    const refetchInterval = useLiveThreadRefetchInterval();

    const {
        data: transcript,
        isLoading,
        error,
    } = useGetMastraThreadTranscript({
        threadId,
        agentId,
        refetchInterval,
    });

    const entries =
        transcript != null
            ? chatMessagesToEntries(
                  transcript.messages,
                  transcript.widgetSources,
              )
            : [];

    return (
        <YStack flex={1} minHeight="70vh" width="100%" overflow="hidden">
            <ScrollView flex={1}>
                <YStack gap="$3" padding="$4">
                    {entries.length > 0 ? (
                        entries.map((entry, index) => (
                            <TranscriptEntry
                                key={index}
                                entry={entry}
                                memberUserId={memberUserId}
                            />
                        ))
                    ) : isLoading ? (
                        <BodySmallRegular color="$gray700">
                            Loading thread...
                        </BodySmallRegular>
                    ) : error != null ? (
                        <BodySmallRegular color="$red900">
                            Unable to load conversation transcript.
                        </BodySmallRegular>
                    ) : (
                        <BodySmallRegular color="$gray700">
                            No messages found for thread.
                        </BodySmallRegular>
                    )}
                </YStack>
            </ScrollView>
        </YStack>
    );
};
